import React, { useState, useEffect, useContext, useCallback } from "react";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCreditCard,
  faSearch,
  faEye,
  faChevronLeft,
  faChevronRight,
  faXmark,
  faSlidersH,
  faCalendarPlus,
  faBan,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { SUBContext } from "../context/subContext";

const STATUS_MAP = {
  ACTIVE: { label: "Active", cls: "bg-green-100 text-green-700" },
  EXPIRED: { label: "Expired", cls: "bg-yellow-100 text-yellow-700" },
  TERMINATED_BY_MS: { label: "Terminated", cls: "bg-red-100 text-red-600" },
};

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

const fmt = (n) =>
  (n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const COLS = ["#", "Matchseeker", "BG Profile", "Period", "Amount", "Status", ""];

const SubscriptionsListing = () => {
  const {
    subSearchTerm: globalSearchTerm,
    setSubSearchTerm: setGlobalSearchTerm,
    subFilters: filters,
    setSubFilters: setFilters,
    ContextCurrentPage: currentPage,
    contextSetCurrentPage: setCurrentPage,
    ContextTotalPages: totalPages,
    contextSetTotalPages: setTotalPages,
    Contextlimit: limit,
    ContextReload: reload,
    ContextSetReload: setReload,
  } = useContext(SUBContext);

  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [localSearch, setLocalSearch] = useState(globalSearchTerm || "");
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [extendDays, setExtendDays] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  const fetchSubs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit };
      const body = globalSearchTerm?.trim()
        ? {}
        : Object.keys(filters).some((k) => filters[k])
        ? { ...filters }
        : {};
      if (globalSearchTerm?.trim()) params.searchTerm = globalSearchTerm.trim();

      const res = await func.post(urls.LIST_ALL_SUCBSCRIPTIONS_URL, body, { params });
      if (res?.status === 200) {
        setSubs(res.data?.data || []);
        setTotalPages(res.data?.totalPages || 1);
      } else {
        toast.error("Failed to load subscriptions");
      }
    } catch {
      toast.error("Network error");
    }
    setLoading(false);
  }, [currentPage, reload]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const triggerSearch = () => {
    setGlobalSearchTerm(localSearch);
    setCurrentPage(1);
    setReload(!reload);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setShowFilter(false);
    setReload(!reload);
  };

  const resetFilters = () => {
    setFilters({ status: "", amountMin: "", amountMax: "", serviceStartDateFrom: "", serviceStartDateTo: "" });
    setLocalSearch("");
    setGlobalSearchTerm("");
    setCurrentPage(1);
    setReload(!reload);
  };

  const handleExtend = async () => {
    if (!extendDays || isNaN(extendDays) || Number(extendDays) < 1) { toast.warn("Enter a valid number of days"); return; }
    setActionLoading(true);
    try {
      const res = await func.post(urls.EXTEND_SUBSCRIPTION_URL + selectedSub._id, { days: Number(extendDays) });
      if (res?.status === 200) {
        toast.success("Subscription extended successfully");
        setShowExtendModal(false);
        setExtendDays("");
        setSelectedSub(null);
        setReload(!reload);
      } else {
        toast.warn("Failed to extend subscription");
      }
    } catch { toast.error("Network error"); }
    setActionLoading(false);
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      const res = await func.post(urls.CANCEL_SUBSCRIPTION_URL + selectedSub._id, {});
      if (res?.status === 200) {
        toast.success("Subscription cancelled successfully");
        setShowCancelModal(false);
        setSelectedSub(null);
        setReload(!reload);
      } else {
        toast.warn("Failed to cancel subscription");
      }
    } catch { toast.error("Network error"); }
    setActionLoading(false);
  };

  const PaginationBar = () => {
    if (totalPages <= 1) return null;
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
      (p) => p >= currentPage - 2 && p <= currentPage + 2
    );
    return (
      <div className="flex items-center justify-center gap-1 mt-5">
        <PagBtn onClick={() => setCurrentPage(1)} disabled={currentPage === 1} label="«" />
        <PagBtn onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} icon={faChevronLeft} />
        {currentPage > 3 && <span className="px-2 text-gray-400">…</span>}
        {pages.map((p) => (
          <button
            key={p}
            onClick={() => setCurrentPage(p)}
            className={`w-8 h-8 rounded-md text-sm font-medium transition ${
              p === currentPage ? "bg-indigo-700 text-white" : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            {p}
          </button>
        ))}
        {currentPage < totalPages - 2 && <span className="px-2 text-gray-400">…</span>}
        <PagBtn onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} icon={faChevronRight} />
        <PagBtn onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} label="»" />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faCreditCard} className="text-white text-xl" />
            <div>
              <h1 className="text-2xl font-bold text-white">Subscriptions</h1>
              <p className="text-white/70 text-sm">All active and historical service plans</p>
            </div>
          </div>
          <button
            onClick={() => setShowFilter(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition border border-white/20"
          >
            <FontAwesomeIcon icon={faSlidersH} />
            Filters
          </button>
        </div>

        {/* Search */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1 max-w-sm">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && triggerSearch()}
              placeholder="Search by name, mobile..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <button
            onClick={triggerSearch}
            className="px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow"
          >
            Search
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {COLS.map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i} className="border-b border-gray-100 animate-pulse">
                      {[...Array(7)].map((_, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : subs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-16 text-center text-gray-400">
                      <FontAwesomeIcon icon={faCreditCard} className="text-3xl mb-3 block mx-auto opacity-20" />
                      No subscriptions found
                    </td>
                  </tr>
                ) : (
                  subs.map((s, i) => {
                    const statusCfg = STATUS_MAP[s.service_plan_status] || { label: s.service_plan_status, cls: "bg-gray-100 text-gray-600" };
                    return (
                      <tr key={s._id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {(currentPage - 1) * limit + i + 1}
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{s.matchseekerName || "—"}</p>
                          <p className="text-xs text-gray-400">{s.matchseekerMobile || ""}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-gray-700">{s.bgFirstName || "—"}</p>
                          <p className="text-xs text-gray-400">{s.bgContact || ""}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                          <p>{fmtDate(s.service_start_date)}</p>
                          <p className="text-gray-400">→ {fmtDate(s.service_end_date)}</p>
                          {s.hasRenewals && s.lastRenewedDate && (
                            <span className="mt-1 inline-block px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">
                              Renewed {fmtDate(s.lastRenewedDate)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-semibold text-gray-800">₹{fmt(s.total)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusCfg.cls}`}>
                            {statusCfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => navigate("/manage-subscription/view", { state: { subId: s._id } })}
                              className="flex items-center gap-1.5 px-2.5 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium transition"
                            >
                              <FontAwesomeIcon icon={faEye} />
                              View
                            </button>
                            <button
                              title="Extend"
                              onClick={() => { setSelectedSub(s); setExtendDays(""); setShowExtendModal(true); }}
                              className="flex items-center justify-center w-7 h-7 text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-md transition"
                            >
                              <FontAwesomeIcon icon={faCalendarPlus} />
                            </button>
                            {s.service_plan_status === "ACTIVE" && (
                              <button
                                title="Cancel"
                                onClick={() => { setSelectedSub(s); setShowCancelModal(true); }}
                                className="flex items-center justify-center w-7 h-7 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded-md transition"
                              >
                                <FontAwesomeIcon icon={faBan} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <PaginationBar />
      </div>

      {/* Extend Modal */}
      {showExtendModal && selectedSub && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowExtendModal(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Extend Subscription</h3>
              <button onClick={() => setShowExtendModal(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">Extending subscription for <span className="font-semibold">{selectedSub.matchseekerName}</span>.</p>
              <label className="block text-xs font-medium text-gray-600 mb-1">Number of Days</label>
              <input
                type="number"
                min={1}
                value={extendDays}
                onChange={(e) => setExtendDays(e.target.value)}
                placeholder="e.g. 30"
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400"
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowExtendModal(false)} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Cancel</button>
              <button onClick={handleExtend} disabled={actionLoading} className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                {actionLoading && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && selectedSub && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowCancelModal(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Cancel Subscription</h3>
              <button onClick={() => setShowCancelModal(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600">Are you sure you want to cancel the subscription for <span className="font-semibold">{selectedSub.matchseekerName}</span>? This action cannot be undone.</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowCancelModal(false)} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Go Back</button>
              <button onClick={handleCancel} disabled={actionLoading} className="flex-1 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                {actionLoading && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilter && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowFilter(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Filter Subscriptions</h3>
              <button onClick={() => setShowFilter(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Plan Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 bg-white"
                >
                  <option value="">All Statuses</option>
                  <option value="ACTIVE">Active</option>
                  <option value="EXPIRED">Expired</option>
                  <option value="TERMINATED_BY_MS">Terminated</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Min Amount (₹)</label>
                  <input
                    type="number"
                    min={0}
                    value={filters.amountMin}
                    placeholder="0"
                    onChange={(e) => setFilters({ ...filters, amountMin: e.target.value })}
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Max Amount (₹)</label>
                  <input
                    type="number"
                    min={0}
                    value={filters.amountMax}
                    placeholder="Any"
                    onChange={(e) => setFilters({ ...filters, amountMax: e.target.value })}
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Start Date (From)</label>
                  <input
                    type="date"
                    value={filters.serviceStartDateFrom}
                    onChange={(e) => setFilters({ ...filters, serviceStartDateFrom: e.target.value })}
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Start Date (To)</label>
                  <input
                    type="date"
                    value={filters.serviceStartDateTo}
                    onChange={(e) => setFilters({ ...filters, serviceStartDateTo: e.target.value })}
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={resetFilters} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                Reset
              </button>
              <button onClick={applyFilters} className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium">
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PagBtn = ({ onClick, disabled, label, icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-8 h-8 flex items-center justify-center rounded-md text-sm transition ${
      disabled ? "text-gray-300 cursor-not-allowed" : "text-gray-600 hover:bg-gray-100"
    }`}
  >
    {icon ? <FontAwesomeIcon icon={icon} className="text-xs" /> : label}
  </button>
);

export default SubscriptionsListing;
